import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldCheck, ArrowUpRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * One-time CROA + Terms consent required after signup, before any protected action.
 * Persists user.consent_at on the backend. Cannot be dismissed without accepting.
 */
export function ConsentModal() {
  const { user, refreshUser } = useAuth();
  const [checks, setChecks] = useState({ tos: false, croa: false, ai: false });
  const [submitting, setSubmitting] = useState(false);

  const needsConsent = !!user && !user.consent_at;

  useEffect(() => {
    // Reset checkboxes when the modal opens fresh
    if (needsConsent) setChecks({ tos: false, croa: false, ai: false });
  }, [needsConsent]);

  const allChecked = checks.tos && checks.croa && checks.ai;

  const accept = async () => {
    if (!allChecked) {
      toast.error("Please acknowledge all three items");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/consent", {});
      if (refreshUser) await refreshUser();
      toast.success("Welcome — you're all set");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={needsConsent}>
      <DialogContent
        className="max-w-lg bg-[#131518] border-[#26282C] text-[#F5F3EE]"
        data-testid="consent-modal"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="w-12 h-12 rounded-sm bg-[#D5FF3F]/10 border border-[#D5FF3F]/20 flex items-center justify-center mb-4">
            <ShieldCheck size={20} className="text-[#D5FF3F]" />
          </div>
          <DialogTitle className="font-display text-3xl leading-tight">
            Before we start<span className="text-[#D5FF3F]">.</span>
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[#A8ADA5] leading-relaxed">
          Aura is a self-service software tool. To keep everything above-board under the Credit
          Repair Organizations Act, we just need three quick acknowledgements from you.
        </p>

        <div className="space-y-4 mt-2">
          <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-tos">
            <Checkbox
              checked={checks.tos}
              onCheckedChange={(v) => setChecks((c) => ({ ...c, tos: !!v }))}
              className="mt-0.5"
            />
            <div className="text-sm">
              I&rsquo;ve read and agree to Aura&rsquo;s{" "}
              <Link to="/app/terms" target="_blank" className="text-[#D5FF3F] underline">
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link to="/app/disclaimer" target="_blank" className="text-[#D5FF3F] underline">
                Privacy &amp; Disclaimer
              </Link>
              .
            </div>
          </label>

          <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-croa">
            <Checkbox
              checked={checks.croa}
              onCheckedChange={(v) => setChecks((c) => ({ ...c, croa: !!v }))}
              className="mt-0.5"
            />
            <div className="text-sm">
              I understand Aura is a <span className="text-[#F5F3EE]">self-service tool</span> — I
              can dispute credit report items myself, for free, at any time, without Aura. I
              retain full control of every letter and every action.
            </div>
          </label>

          <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-ai">
            <Checkbox
              checked={checks.ai}
              onCheckedChange={(v) => setChecks((c) => ({ ...c, ai: !!v }))}
              className="mt-0.5"
            />
            <div className="text-sm">
              I understand AI-drafted letters may contain errors. I&rsquo;ll{" "}
              <span className="text-[#F5F3EE]">review every letter</span> before it&rsquo;s mailed
              and Aura does not guarantee any specific outcome.
            </div>
          </label>
        </div>

        <Button
          onClick={accept}
          disabled={!allChecked || submitting}
          data-testid="consent-accept-btn"
          className="w-full mt-2 h-11 rounded-full btn-lime"
        >
          {submitting ? "Saving…" : (<>I understand — let&rsquo;s go <ArrowUpRight size={14} className="ml-1.5" /></>)}
        </Button>
        <p className="text-[10px] text-[#6D716C] font-mono uppercase tracking-widest text-center mt-1">
          Your consent is timestamped and saved securely.
        </p>
      </DialogContent>
    </Dialog>
  );
}
